import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IDSButton, IDSInput, IDSAccordion,
  IDSPuffList, IDSPuffListItem, IDSPuffListItemHeader, IDSTag,
} from '@inera/ids-react';
import ConfirmDialog from '../ConfirmDialog';
import StatusAlert from '../StatusAlert';
import {
  fetchOrganisations,
  updateOrganisation,
  deleteOrganisation,
} from '../../services/organisationsAdminAPI';
import { fetchAllUsers } from '../../services/usersAdminAPI';
import { formatRoleName } from '../../utils/tokenUtils';
import { validateOrganisation } from '../../utils/adminValidation';

// ── Organisation form (edit) ──────────────────────────────────────────────────
const OrgForm = ({ initial = {}, onSave, onCancel, submitLabel = 'Spara' }) => {
  const [name, setName] = useState(initial.name || '');
  const [domain, setDomain] = useState(initial.domain || '');
  const [hsaId, setHsaId] = useState(initial.hsaId || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateOrganisation({ name, domain, hsaId });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setLoading(true);
    setError(null);
    try {
      await onSave({ organisationName: name.trim(), organisationDomain: domain.trim(), hsaId: hsaId.trim() });
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate style={{ background: 'var(--ids-palette-neutral-95)', border: '1px solid var(--ids-palette-neutral-90)', borderRadius: '6px', padding: '1.25rem', marginBottom: '1rem' }}>
      <div className="ids-mb-3">
        <IDSInput
          label="Organisationsnamn *"
          type="text"
          value={name}
          invalid={!!fieldErrors.name}
          errorMsg={fieldErrors.name}
          onChange={e => { setName(e.target.value); setFieldErrors(prev => ({ ...prev, name: null })); }}
        />
      </div>
      <div className="ids-mb-3">
        <IDSInput
          label="Domän"
          type="text"
          value={domain}
          invalid={!!fieldErrors.domain}
          errorMsg={fieldErrors.domain}
          onChange={e => { setDomain(e.target.value); setFieldErrors(prev => ({ ...prev, domain: null })); }}
        />
      </div>
      <div className="ids-mb-3">
        <IDSInput
          label="HSA-ID"
          type="text"
          value={hsaId}
          invalid={!!fieldErrors.hsaId}
          errorMsg={fieldErrors.hsaId}
          placeholder="T.ex. SE165565594230-1003"
          onChange={e => { setHsaId(e.target.value); setFieldErrors(prev => ({ ...prev, hsaId: null })); }}
        />
        <p style={{ fontSize: '0.8rem', color: 'var(--ids-palette-neutral-50)', marginTop: '0.25rem' }}>
          HSA-ID börjar med SE följt av organisationsnumret och ett enhetsnummer.
        </p>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <IDSButton size="s" type="submit" disabled={loading}>{loading ? 'Sparar...' : submitLabel}</IDSButton>
        <IDSButton size="s" type="button" onClick={onCancel}>Avbryt</IDSButton>
      </div>
      <StatusAlert error={error} className="ids-mt-3" />
    </form>
  );
};

// ── Read-only user list, navigates to UsersPage ──────────────────────────────
const UserPuffList = ({ users, onNavigate }) => (
  <IDSPuffList className="ids-mb-3">
    {users.map(user => (
      <IDSPuffListItem
        key={user.keycloakUserId}
        header={
          <IDSPuffListItemHeader headlineLevel={5} noMargin>
            {user.username}
          </IDSPuffListItemHeader>
        }
        itemLink={
          <a
            href="/users"
            title="Gå till användaradministration"
            onClick={(e) => { e.preventDefault(); onNavigate(); }}
          />
        }
        extra={(user.roles || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {user.roles.map(role => (
              <IDSTag key={role.id}>{formatRoleName(role.name)}</IDSTag>
            ))}
          </div>
        )}
      >
        {user.email}
      </IDSPuffListItem>
    ))}
  </IDSPuffList>
);

// ── Organisation accordion ────────────────────────────────────────────────────
const OrgAccordion = ({ org, users, onOrgUpdate, onOrgDelete }) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const handleSaveOrg = async (data) => {
    await updateOrganisation(org.id, data);
    onOrgUpdate(org.id, { name: data.organisationName, hsaId: data.hsaId, domain: data.organisationDomain });
    setEditing(false);
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await deleteOrganisation(org.id);
      onOrgDelete(org.id);
    } catch (err) {
      setError(err.message || "Något gick fel. Försök igen.");
      setDeleting(false);
    }
  };

  return (
    <>
      {confirmDelete && (
        <ConfirmDialog
          message={`Vill du ta bort organisationen "${org.name}" och alla dess roller?`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <IDSAccordion
        headline={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3>{org.name}</h3>
            <span style={{ fontSize: '0.85rem', color: 'var(--ids-palette-neutral-60)' }}>
              {`${users.length} användare`}
              {org.hsaId ? ` · HSA: ${org.hsaId}` : ''}
              {org.domain ? ` · ${org.domain}` : ''}
            </span>
          </div>
        }
        headlineSize="m"
        level={1}
      >
        <div>
          {/* Organisation actions */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <IDSButton size="s" type="button" onClick={() => setEditing(e => !e)}>
              {editing ? 'Avbryt redigering' : 'Redigera organisation'}
            </IDSButton>
            <IDSButton size="s" type="button" disabled={deleting} onClick={() => setConfirmDelete(true)}>
              {deleting ? '...' : 'Ta bort organisation'}
            </IDSButton>
          </div>

          <StatusAlert error={error} className="ids-mb-3" />

          {editing && (
            <OrgForm
              initial={{ name: org.name, domain: org.domain, hsaId: org.hsaId }}
              onSave={handleSaveOrg}
              onCancel={() => setEditing(false)}
              submitLabel="Uppdatera"
            />
          )}

          {/* Users (read-only, managed on UsersPage) */}
          <h4 className="ids-heading-s ids-mb-2">Användare</h4>
          {users.length === 0 ? (
            <p className="ids-mb-3" style={{ color: 'var(--ids-palette-neutral-70)', fontSize: '0.9rem' }}>Inga användare i denna organisation.</p>
          ) : (
            <UserPuffList users={users} onNavigate={() => navigate('/users')} />
          )}
        </div>
      </IDSAccordion>
    </>
  );
};

// ── Main layout ───────────────────────────────────────────────────────────────
const OrganisationsAdminLayout = () => {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [orgsData, usersData] = await Promise.all([fetchOrganisations(), fetchAllUsers()]);
        setOrgs(Array.isArray(orgsData) ? orgsData : []);
        setUsers(usersData);
      } catch (err) {
        setError(err.message || "Något gick fel. Försök igen.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const usersForOrg = (orgName) =>
    users.filter(u => (u.organisations || []).includes(orgName));

  const handleOrgUpdate = (orgId, patch) => {
    const oldName = orgs.find(o => o.id === orgId)?.name;
    setOrgs(prev => prev.map(o => o.id === orgId ? { ...o, ...patch } : o));

    // Keep users' org names and role-name prefixes in sync after a rename
    if (patch.name && oldName && patch.name !== oldName) {
      const oldPrefix = `${oldName.replace(/ /g, '_')}-`;
      const newPrefix = `${patch.name.replace(/ /g, '_')}-`;
      setUsers(prev => prev.map(u => ({
        ...u,
        organisations: (u.organisations || []).map(n => n === oldName ? patch.name : n),
        roles: (u.roles || []).map(r =>
          r.name?.startsWith(oldPrefix) ? { ...r, name: newPrefix + r.name.slice(oldPrefix.length) } : r
        ),
      })));
    }
  };

  const handleOrgDelete = (orgId) => {
    setOrgs(prev => prev.filter(o => o.id !== orgId));
  };

  return (
    <main className="ids-content ids-p-6" style={{ maxWidth: '860px', margin: '2rem auto' }}>
      <h1 className="ids-heading-l ids-mb-2">Organisationer</h1>

      <section className="ids-mt-6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="ids-heading-m">Alla organisationer</h2>
          <IDSButton size="s" type="button" onClick={() => navigate('/createorganisation')}>+ Skapa ny organisation</IDSButton>
        </div>

        {loading && <p>Laddar...</p>}
        <StatusAlert error={error} />
        {!loading && !error && orgs.length === 0 && (
          <p style={{ color: 'var(--ids-palette-neutral-60)' }}>Inga organisationer hittades.</p>
        )}

        {orgs.map(org => (
          <OrgAccordion
            key={org.id}
            org={org}
            users={usersForOrg(org.name)}
            onOrgUpdate={handleOrgUpdate}
            onOrgDelete={handleOrgDelete}
          />
        ))}
      </section>
    </main>
  );
};

export default OrganisationsAdminLayout;
